import { useTerminology } from '../../../terminology/TerminologyContext';
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import ThemeContext from '../../ThemeContext';
import Pagination from '../../Utils/Pagination';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { FULL_ROUTES, URL_SEGMENTS, buildPath } from '../../../config/Routesconfig';

const Admins = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Typography from theme
  const fontFamily =
    theme.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const bodyFontSize = theme.typography?.bodyFontSize || 14;
  const titleFontSize = theme.typography?.titleFontSize || 24;

  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedUserId, setHighlightedUserId] = useState(null);
  const [highlightedField, setHighlightedField] = useState(null);
  const [hoveredUserId, setHoveredUserId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [gotoPage, setGotoPage] = useState('');
  const [sortField, setSortField] = useState('admin_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showModal, setShowModal] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Derived absolute paths from config
  const addAdminPath  = FULL_ROUTES.superadminAbsoluteRoutes.addAdmin;
  const editAdminPath = FULL_ROUTES.superadminAbsoluteRoutes.editAdmin; // contains :id
  const adminsListPath = `/${URL_SEGMENTS.superadmin}/${URL_SEGMENTS.admins}`;

  // Fetch admins
  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/webmanager/admins`,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          }
        );

        const admins = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : [];

        setUsers(admins);
      } catch (error) {
        toast.error('Failed to fetch admins.');
      }
    };
    fetchAdmins();
  }, []);

  /* ---------- Search & Sort ---------- */

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setHighlightedUserId(null);
    setHighlightedField(null);
    setCurrentPage(1);
  };

  const handleSearchSubmit = () => {
    const matched = users.find(
      (u) =>
        u.id?.toString() === searchTerm ||
        u.admin_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (matched) {
      setHighlightedUserId(matched.id);
      setHighlightedField(
        matched.id?.toString() === searchTerm ? 'id' : 'admin_name'
      );
    } else {
      setHighlightedUserId(null);
      setHighlightedField(null);
    }
  };

  const handleSort = (field) => {
    setSortOrder(sortField === field && sortOrder === 'asc' ? 'desc' : 'asc');
    setSortField(field);
  };

  const getSortValue = (user, field) => {
    if (field === 'id') return user.id || 0;
    return (user[field] || '').toString().toLowerCase();
  };

  const handleDelete = (id) => {
    setSelectedUserId(id);
    setShowModal(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/webmanager/admins/${selectedUserId}`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
      );
      setUsers((prev) => prev.filter((u) => u.id !== selectedUserId));
      toast.success('Admin deleted successfully.');
    } catch {
      toast.error('Failed to delete admin.');
    }
    setShowModal(false);
  };

  /* ---------- Data ---------- */

  const filteredUsers = users.filter(
    (u) =>
      u.admin_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.id?.toString().includes(searchTerm)
  );

  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const va = getSortValue(a, sortField);
    const vb = getSortValue(b, sortField);
    return sortOrder === 'asc' ? (va > vb ? 1 : -1) : va < vb ? 1 : -1;
  });

  const paginatedUsers = sortedUsers.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  /* ---------- UI ---------- */

  return (
    <div style={{ fontFamily, fontSize: `${bodyFontSize}px` }}>
      {/* WHITE CARD CONTAINER */}
      <div
        style={{
          backgroundColor: "#ffffff",
          padding: "1rem",
          borderRadius: "10px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          fontFamily,
          marginTop: "10px",
        }}
      >
        {/* HEADER */}
        <div
          style={{
            display: 'flex',
            flexDirection: window.innerWidth < 600 ? 'column' : 'row',
            alignItems: window.innerWidth < 600 ? 'stretch' : 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
            gap: '0.5rem',
          }}
        >
          <h4
            style={{
              fontSize: `${titleFontSize}px`,
              fontWeight: 'bold',
              color: theme.colors.textPrimary,
              marginBottom: 0,
              fontFamily,
            }}
          >
            Admins Management
          </h4>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              flexWrap: 'wrap',
              justifyContent: window.innerWidth < 600 ? 'stretch' : 'flex-end',
            }}
          >
            {/* Search Input */}
            <div style={{ position: "relative", maxWidth: "140px", height: "38px" }}>
              <input
                type="text"
                placeholder="Search admins..."
                value={searchTerm}
                onChange={handleSearch}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "1px solid #dee2e6",
                  borderRadius: "25px",
                  outline: "none",
                  padding: "0 40px 0 14px",
                  fontSize: "12px",
                  backgroundColor: "#ffffff",
                  fontFamily,
                }}
              />
              <button
                onClick={handleSearchSubmit}
                style={{
                  position: "absolute",
                  right: "2px",
                  top: "2px",
                  width: "34px",
                  height: "34px",
                  background: theme.gradients?.primary || theme.colors.primary,
                  border: "none",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-search" style={{ color: "#fff", fontSize: "14px" }}></i>
              </button>
            </div>

            {/* Add Admin — path from RoutesConfig */}
            <Link
              to={addAdminPath}
              style={{
                background: theme.gradients?.secondary || theme.colors.secondary,
                color: theme.colors.textWhite,
                padding: "6px 12px",
                borderRadius: "8px",
                fontSize: "clamp(10px, 2.5vw, 11px)",
                textDecoration: "none",
                minWidth: "120px",
                textAlign: "center",
                fontFamily,
              }}
            >
              Add Admin
            </Link>
          </div>
        </div>

        {/* TOP PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* TABLE CONTAINER */}
        <div
          style={{
            overflowX: "auto",
            backgroundColor: "#ffffff",
            borderRadius: "10px",
            border: "1px solid #dee2e6",
            marginTop: "1rem",
            marginBottom: "1rem",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: `${bodyFontSize}px`,
              fontFamily,
            }}
          >
            <thead>
              <tr style={{ backgroundColor: theme.colors.primary, color: theme.colors.textWhite }}>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('id')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    ID
                    {sortField === 'id' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ cursor: 'pointer' }} onClick={() => handleSort('admin_name')}>
                  <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                    Admin Name
                    {sortField === 'admin_name' && (
                      <i className={`bi ${sortOrder === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down'} ms-1`}></i>
                    )}
                  </span>
                </th>
                <th className="p-2" style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length > 0 ? (
                paginatedUsers.map((u) => (
                  <tr
                    key={u.id}
                    onMouseEnter={() => setHoveredUserId(u.id)}
                    onMouseLeave={() => setHoveredUserId(null)}
                    style={{ borderBottom: "1px solid #dee2e6" }}
                  >
                    <td className="p-2" style={{ backgroundColor: u.id === highlightedUserId && highlightedField === 'id' ? '#FFD700' : '#fff' }}>
                      {u.id}
                    </td>
                    <td className="p-2" style={{ backgroundColor: u.id === highlightedUserId && highlightedField === 'admin_name' ? '#FFD700' : '#fff' }}>
                      {u.admin_name || 'N/A'}
                    </td>
                    <td className="p-2 position-relative">
                      {hoveredUserId === u.id && (
                        <div
                          style={{
                            position: "absolute",
                            right: "0.5rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            display: "flex",
                            gap: "0.5rem",
                          }}
                        >
                          {/* Edit — path from RoutesConfig, :id replaced via buildPath */}
                          <Link
                            to={buildPath(editAdminPath, { id: u.id })}
                            style={{
                              color: theme.colors.primary,
                              textDecoration: "underline",
                              fontSize: `${bodyFontSize}px`,
                            }}
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(u.id)}
                            style={{
                              background: "none",
                              border: "none",
                              color: theme.colors.danger,
                              textDecoration: "underline",
                              cursor: "pointer",
                              fontSize: `${bodyFontSize}px`,
                              padding: 0,
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="text-center p-3" colSpan={3}>
                    No admins found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* BOTTOM PAGINATION */}
        <Pagination
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          perPage={perPage}
          setPerPage={setPerPage}
          totalItems={sortedUsers.length}
          gotoPage={gotoPage}
          setGotoPage={setGotoPage}
        />

        {/* DELETE CONFIRMATION MODAL */}
        {showModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "320px",
                textAlign: "center",
                fontFamily,
              }}
            >
              <h5 style={{ marginBottom: "10px", color: theme.colors.danger, fontSize: `${titleFontSize}px`, fontFamily }}>
                Confirm Delete
              </h5>
              <p style={{ fontSize: `${bodyFontSize}px`, fontFamily }}>
                Are you sure you want to delete this admin?
              </p>
              <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    backgroundColor: theme.colors.secondary,
                    color: theme.colors.textWhite,
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    fontFamily,
                    fontSize: `${bodyFontSize}px`,
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    backgroundColor: theme.colors.danger,
                    color: theme.colors.textWhite,
                    border: "none",
                    padding: "8px 16px",
                    borderRadius: "5px",
                    fontFamily,
                    fontSize: `${bodyFontSize}px`,
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admins;